import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  SafeAreaView,
  ActivityIndicator,
  StyleSheet,
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  Modal,
  FlatList,
  Platform,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Calendar } from 'react-native-calendars';
import ShimmerPlaceHolder from 'react-native-shimmer-placeholder';
import { LinearGradient } from 'expo-linear-gradient';

const PublicBooking = ({ route, navigation }) => {
  const collabID = route?.params?.CollabID || '';
  const brandNameFromRoute = route?.params?.BrandName || '';

  const API_BASE = 'https://collabbay.com/APIControllers';
  const IMAGE_BASE = 'https://collabbayadmin.com/listings/';
  const FALLBACK_BANNER =
    'https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1';

  const SCREEN_WIDTH = Dimensions.get('window').width;
  const MAX_WIDTH = 640;
  const CONTENT_WIDTH = Math.min(SCREEN_WIDTH, MAX_WIDTH);
  const BANNER_WIDTH = CONTENT_WIDTH - 32;
  const BANNER_HEIGHT = 245;

  const bannerListRef = useRef(null);
  const successScaleAnim = useRef(new Animated.Value(0)).current;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [error, setError] = useState('');

  const [bannerImages, setBannerImages] = useState([FALLBACK_BANNER]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [bannerLoaded, setBannerLoaded] = useState(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const [dateModalVisible, setDateModalVisible] = useState(false);
  const [timeModalVisible, setTimeModalVisible] = useState(false);
  const [partyModalVisible, setPartyModalVisible] = useState(false);

  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const currentYear = new Date().getFullYear();

  const partyOptions = [1, 2, 3, 4, 5, 6, 7, 8];
  const timeOptions = [
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
    '21:00',
    '21:30',
  ];

  const parseBannerImages = (csv) => {
    if (!csv || !csv.trim()) return [FALLBACK_BANNER];

    const arr = csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((u) => (u.startsWith('http') ? u : IMAGE_BASE + u));

    return arr.length ? arr : [FALLBACK_BANNER];
  };

  const getTodayYMD = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatReadableDate = (ymd) => {
    if (!ymd) return 'Select date';
    const [y, m, d] = ymd.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const buildBooksDate = () => {
    if (!selectedDate || !selectedTime) return '';
    return `${selectedDate} ${selectedTime}:00`;
  };

  const formValid = useMemo(() => {
    return (
      fullName.trim() !== '' &&
      email.trim() !== '' &&
      phoneNumber.trim() !== '' &&
      selectedDate.trim() !== '' &&
      selectedTime.trim() !== ''
    );
  }, [fullName, email, phoneNumber, selectedDate, selectedTime]);

  useEffect(() => {
    if (!collabID) {
      setError('Missing CollabID.');
      setLoading(false);
      return;
    }

    fetchOffer();
  }, [collabID]);

  useEffect(() => {
    if (successModalVisible) {
      successScaleAnim.setValue(0);
      Animated.spring(successScaleAnim, {
        toValue: 1,
        friction: 5,
        tension: 75,
        useNativeDriver: true,
      }).start();
    }
  }, [successModalVisible, successScaleAnim]);

  useEffect(() => {
    if (bannerImages.length <= 1) return;

    const interval = setInterval(() => {
      setBannerIndex((prev) => {
        const next = (prev + 1) % bannerImages.length;
        bannerListRef.current?.scrollToOffset({
          offset: next * BANNER_WIDTH,
          animated: true,
        });
        return next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [bannerImages.length, BANNER_WIDTH]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      setError('');
      setBannerLoaded(false);

      const response = await fetch(
        `${API_BASE}/GetVenueBookingOfferForPublic.php?CollabID=${encodeURIComponent(collabID)}`
      );

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to fetch offer.');
      }

      setOfferData(json.data);
      const parsedImages = parseBannerImages(json?.data?.LogoImage || '');
      setBannerImages(parsedImages);
      setBannerIndex(0);
    } catch (err) {
      setError(err.message || 'Failed to load booking page.');
    } finally {
      setLoading(false);
    }
  };

  const onBannerScrollEnd = (e) => {
    const x = e?.nativeEvent?.contentOffset?.x || 0;
    const idx = Math.round(x / BANNER_WIDTH);
    if (!Number.isNaN(idx)) setBannerIndex(idx);
  };

  const scrollToBanner = (index) => {
    if (!bannerListRef.current || bannerImages.length === 0) return;
    const safeIndex = ((index % bannerImages.length) + bannerImages.length) % bannerImages.length;

    bannerListRef.current.scrollToOffset({
      offset: safeIndex * BANNER_WIDTH,
      animated: true,
    });

    setBannerIndex(safeIndex);
  };

  const handleSubmit = async () => {
    if (!formValid) {
      setErrorMessage('Please complete all required details before booking.');
      setErrorModalVisible(true);
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE}/InsertPublicBooking.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          CollabID: collabID,
          Name: fullName.trim(),
          Email: email.trim(),
          Number: phoneNumber.trim(),
          PlusOnes: Number(partySize),
          BooksDate: buildBooksDate(),
        }),
      });

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to submit booking.');
      }

      setSuccessMessage('Your request has been sent to the venue successfully.');
      setSuccessModalVisible(true);

      setFullName('');
      setEmail('');
      setPhoneNumber('');
      setPartySize(2);
      setSelectedDate('');
      setSelectedTime('');
    } catch (err) {
      setErrorMessage(err.message || 'Something went wrong.');
      setErrorModalVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderHero = () => (
    <View style={styles.heroSection}>
      <FlatList
        ref={bannerListRef}
        data={bannerImages}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        keyExtractor={(item, index) => `${item}-${index}`}
        onMomentumScrollEnd={onBannerScrollEnd}
        getItemLayout={(_, index) => ({
          length: BANNER_WIDTH,
          offset: BANNER_WIDTH * index,
          index,
        })}
        style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, alignSelf: 'center' }}
        renderItem={({ item }) => (
          <ShimmerPlaceHolder
            LinearGradient={LinearGradient}
            visible={bannerLoaded}
            shimmerColors={['#e0e0e0', '#f3f3f3', '#e0e0e0']}
            style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, borderRadius: 24 }}
          >
            <Image
              source={{ uri: item }}
              style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, borderRadius: 24 }}
              resizeMode="cover"
              onLoad={() => setBannerLoaded(true)}
              onError={() => setBannerLoaded(true)}
            />
          </ShimmerPlaceHolder>
        )}
      />

      {navigation?.canGoBack?.() ? (
        <View style={styles.backContainer}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={22} color="#111" />
          </TouchableOpacity>
        </View>
      ) : null}

      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.55)']}
        style={styles.heroOverlay}
        pointerEvents="none"
      />

      <View style={styles.heroInfoWrap}>
        <Text style={styles.heroVenueName}>{offerData?.BrandName}</Text>

        {!!offerData?.Location && (
          <View style={styles.heroLocationPill}>
            <Ionicons name="location-sharp" size={14} color="#fff" />
            <Text style={styles.heroLocationText}>{offerData.Location}</Text>
          </View>
        )}
      </View>

      {bannerImages.length > 1 && (
        <>
          <TouchableOpacity
            style={styles.bannerArrowLeft}
            onPress={() => scrollToBanner(bannerIndex - 1)}
            activeOpacity={0.9}
          >
            <Ionicons name="chevron-back" size={18} color="#111" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.bannerArrowRight}
            onPress={() => scrollToBanner(bannerIndex + 1)}
            activeOpacity={0.9}
          >
            <Ionicons name="chevron-forward" size={18} color="#111" />
          </TouchableOpacity>

          <View style={styles.dotsWrap}>
            {bannerImages.map((_, i) => (
              <TouchableOpacity key={i} onPress={() => scrollToBanner(i)} activeOpacity={0.8}>
                <View style={[styles.dot, i === bannerIndex && styles.dotActive]} />
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}
    </View>
  );

  const renderBookingCard = () => (
    <View style={styles.bookingCard}>
      <View style={styles.sectionHeaderRow}>
        <View style={styles.sectionHeaderLeft}>
          <Text style={styles.sectionEyebrow}>Exclusive offer</Text>
          <Text style={styles.offerTitle}>Book this experience</Text>
        </View>
        <View style={styles.creatorSourcePill}>
          <Ionicons name="sparkles-outline" size={14} color="#111" />
          <Text style={styles.creatorSourceText}>via creator</Text>
        </View>
      </View>

      <View style={styles.offerHighlightBox}>
        <Text style={styles.offerHighlightText}>
          {offerData?.VenuePublicOffer || 'Offer details unavailable'}
        </Text>
      </View>

      <Text style={styles.sectionDescription}>
        Choose your party size, pick a date, select a time, and send a booking request in seconds.
      </Text>

      <View style={styles.quickSelectorsRow}>
        <TouchableOpacity style={styles.selectorCard} onPress={() => setPartyModalVisible(true)} activeOpacity={0.88}>
          <Text style={styles.selectorLabel}>Party size</Text>
          <View style={styles.selectorValueRow}>
            <Text style={styles.selectorValue}>{partySize} {partySize === 1 ? 'guest' : 'guests'}</Text>
            <Ionicons name="chevron-down" size={18} color="#111" />
          </View>
        </TouchableOpacity>

        <TouchableOpacity style={styles.selectorCard} onPress={() => setDateModalVisible(true)} activeOpacity={0.88}>
          <Text style={styles.selectorLabel}>Date</Text>
          <View style={styles.selectorValueRow}>
            <Text
              style={[
                styles.selectorValue,
                !selectedDate && styles.selectorPlaceholder,
              ]}
              numberOfLines={1}
            >
              {selectedDate ? formatReadableDate(selectedDate) : 'Select date'}
            </Text>
            <Ionicons name="calendar-outline" size={18} color="#111" />
          </View>
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={styles.timeSelectorCard} onPress={() => setTimeModalVisible(true)} activeOpacity={0.88}>
        <Text style={styles.selectorLabel}>Time</Text>
        <View style={styles.selectorValueRow}>
          <Text style={[styles.selectorValue, !selectedTime && styles.selectorPlaceholder]}>
            {selectedTime || 'Select time'}
          </Text>
          <Ionicons name="time-outline" size={18} color="#111" />
        </View>
      </TouchableOpacity>
    </View>
  );

  const renderGuestDetailsCard = () => (
    <View style={styles.formCard}>
      <Text style={styles.formCardTitle}>Guest details</Text>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Full name</Text>
        <TextInput
          style={styles.inputField}
          placeholder="Enter your name"
          placeholderTextColor="#8e8e93"
          value={fullName}
          onChangeText={setFullName}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Email</Text>
        <TextInput
          style={styles.inputField}
          placeholder="Enter your email"
          placeholderTextColor="#8e8e93"
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Phone number</Text>
        <TextInput
          style={styles.inputField}
          placeholder="Enter your phone number"
          placeholderTextColor="#8e8e93"
          value={phoneNumber}
          onChangeText={setPhoneNumber}
          keyboardType="phone-pad"
        />
      </View>
    </View>
  );

  const renderSummaryCard = () => (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryTitle}>Booking summary</Text>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Venue</Text>
        <Text style={styles.summaryValue}>{offerData?.BrandName || '—'}</Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Date</Text>
        <Text style={styles.summaryValue}>{selectedDate ? formatReadableDate(selectedDate) : 'Not selected'}</Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Time</Text>
        <Text style={styles.summaryValue}>{selectedTime || 'Not selected'}</Text>
      </View>

      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Party size</Text>
        <Text style={styles.summaryValue}>{partySize} {partySize === 1 ? 'guest' : 'guests'}</Text>
      </View>

      <TouchableOpacity
        style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={submitting}
        activeOpacity={0.92}
      >
        {submitting ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <>
            <Text style={styles.submitButtonText}>Request booking</Text>
            <Text style={styles.submitButtonSubtext}>Fast, simple, no sign up required</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderFooter = () => (
    <View style={styles.footerWrap}>
      <Text style={styles.footerLogo}>
        <Text style={{ fontWeight: '300' }}>collab</Text>
        bay
        <Text style={styles.footerDot}>.</Text>
      </Text>
      <Text style={styles.footerText}>© {currentYear} Collabbay. All rights reserved.</Text>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#111" />
          <Text style={styles.loadingText}>Loading booking page...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !offerData) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <View style={styles.errorCard}>
            <Ionicons name="alert-circle-outline" size={46} color="#111" />
            <Text style={styles.errorTitle}>This booking page is unavailable</Text>
            <Text style={styles.errorText}>{error || 'Offer not found.'}</Text>
            {!!brandNameFromRoute && (
              <Text style={styles.errorBrand}>{brandNameFromRoute}</Text>
            )}

            <TouchableOpacity style={styles.retryButton} onPress={fetchOffer}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {renderHero()}
        {renderBookingCard()}
        {renderGuestDetailsCard()}
        {renderSummaryCard()}
        {renderFooter()}
      </ScrollView>

      {/* DATE MODAL */}
      <Modal
        visible={dateModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDateModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.sheetCard}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select date</Text>
              <TouchableOpacity onPress={() => setDateModalVisible(false)}>
                <Ionicons name="close" size={24} color="#111" />
              </TouchableOpacity>
            </View>

            <Calendar
              minDate={getTodayYMD()}
              onDayPress={(day) => {
                setSelectedDate(day.dateString);
                setDateModalVisible(false);
              }}
              markedDates={{
                ...(selectedDate
                  ? {
                      [selectedDate]: {
                        selected: true,
                        selectedColor: '#111827',
                      },
                    }
                  : {}),
              }}
              renderArrow={(direction) => (
                <Ionicons
                  name={direction === 'left' ? 'chevron-back' : 'chevron-forward'}
                  size={22}
                  color="#111"
                />
              )}
              theme={{
                backgroundColor: '#ffffff',
                calendarBackground: '#ffffff',
                textSectionTitleColor: '#6b7280',
                selectedDayBackgroundColor: '#111827',
                selectedDayTextColor: '#ffffff',
                todayTextColor: '#111827',
                dayTextColor: '#111827',
                textDisabledColor: '#d1d5db',
                monthTextColor: '#111827',
                arrowColor: '#111827',
                textDayFontWeight: '500',
                textMonthFontWeight: '700',
                textDayHeaderFontWeight: '600',
              }}
            />
          </View>
        </View>
      </Modal>

      {/* TIME MODAL */}
      <Modal
        visible={timeModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setTimeModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.sheetCard}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select time</Text>
              <TouchableOpacity onPress={() => setTimeModalVisible(false)}>
                <Ionicons name="close" size={24} color="#111" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.timeScrollArea} showsVerticalScrollIndicator={false}>
              {timeOptions.map((time) => {
                const selected = selectedTime === time;
                return (
                  <TouchableOpacity
                    key={time}
                    style={[styles.timeOption, selected && styles.timeOptionSelected]}
                    onPress={() => {
                      setSelectedTime(time);
                      setTimeModalVisible(false);
                    }}
                  >
                    <Text style={[styles.timeOptionText, selected && styles.timeOptionTextSelected]}>
                      {time}
                    </Text>
                    {selected && <Ionicons name="checkmark" size={18} color="#111" />}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* PARTY SIZE MODAL */}
      <Modal
        visible={partyModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setPartyModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.sheetCard}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select party size</Text>
              <TouchableOpacity onPress={() => setPartyModalVisible(false)}>
                <Ionicons name="close" size={24} color="#111" />
              </TouchableOpacity>
            </View>

            <View style={styles.partyGrid}>
              {partyOptions.map((size) => {
                const selected = partySize === size;
                return (
                  <TouchableOpacity
                    key={size}
                    style={[styles.partyOption, selected && styles.partyOptionSelected]}
                    onPress={() => {
                      setPartySize(size);
                      setPartyModalVisible(false);
                    }}
                    activeOpacity={0.88}
                  >
                    <Text style={[styles.partyOptionText, selected && styles.partyOptionTextSelected]}>
                      {size}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>

      {/* SUCCESS MODAL */}
      <Modal
        animationType="fade"
        transparent
        visible={successModalVisible}
        onRequestClose={() => setSuccessModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <Animated.View style={[styles.successCard, { transform: [{ scale: successScaleAnim }] }]}>
            <Ionicons name="checkmark-circle" size={72} color="#22c55e" style={{ marginBottom: 14 }} />
            <Text style={styles.successTitle}>Booking request sent</Text>
            <Text style={styles.successSubtitle}>{successMessage}</Text>

            <TouchableOpacity style={styles.successButton} onPress={() => setSuccessModalVisible(false)}>
              <Text style={styles.successButtonText}>OK</Text>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </Modal>

      {/* ERROR MODAL */}
      <Modal
        animationType="fade"
        transparent
        visible={errorModalVisible}
        onRequestClose={() => setErrorModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.errorModalCard}>
            <Ionicons name="alert-circle-outline" size={46} color="#ef4444" />
            <Text style={styles.errorModalText}>{errorMessage}</Text>
            <TouchableOpacity style={styles.errorModalButton} onPress={() => setErrorModalVisible(false)}>
              <Text style={styles.errorModalButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

export default PublicBooking;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f7f7f8',
  },

  scrollView: {
    flex: 1,
  },

  contentContainer: {
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 34,
    alignSelf: 'center',
    width: '100%',
    maxWidth: 640,
  },

  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    padding: 24,
  },

  loadingText: {
    marginTop: 12,
    color: '#555',
    fontSize: 15,
  },

  heroSection: {
    position: 'relative',
    marginBottom: 18,
  },

  heroOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 120,
    borderRadius: 24,
  },

  heroInfoWrap: {
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 18,
    zIndex: 3,
  },

  heroVenueName: {
    fontSize: 28,
    fontWeight: '800',
    color: '#fff',
    marginBottom: 8,
  },

  heroLocationPill: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
  },

  heroLocationText: {
    marginLeft: 6,
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },

  backContainer: {
    position: 'absolute',
    top: 14,
    left: 14,
    zIndex: 4,
  },

  backButton: {
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.96)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
    elevation: 3,
  },

  bannerArrowLeft: {
    position: 'absolute',
    top: '50%',
    left: 10,
    transform: [{ translateY: -18 }],
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },

  bannerArrowRight: {
    position: 'absolute',
    top: '50%',
    right: 10,
    transform: [{ translateY: -18 }],
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },

  dotsWrap: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 4,
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.45)',
    marginHorizontal: 3,
  },

  dotActive: {
    width: 16,
    borderRadius: 99,
    backgroundColor: '#fff',
  },

  bookingCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    borderWidth: 1,
    borderColor: '#ececec',
    marginBottom: 14,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 2,
  },

  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
  },

  sectionHeaderLeft: {
    flex: 1,
    paddingRight: 12,
  },

  sectionEyebrow: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
  },

  offerTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#111827',
    lineHeight: 28,
  },

  creatorSourcePill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },

  creatorSourceText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
    textTransform: 'lowercase',
  },

  offerHighlightBox: {
    backgroundColor: '#FDF05D',
    borderRadius: 18,
    padding: 16,
    marginBottom: 12,
  },

  offerHighlightText: {
    fontSize: 16,
    color: '#111827',
    lineHeight: 23,
    fontWeight: '600',
  },

  sectionDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 16,
  },

  quickSelectorsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },

  selectorCard: {
    width: '48.4%',
    backgroundColor: '#fafafa',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#ededed',
    padding: 14,
  },

  timeSelectorCard: {
    backgroundColor: '#fafafa',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#ededed',
    padding: 14,
  },

  selectorLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 8,
  },

  selectorValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  selectorValue: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
    flex: 1,
    paddingRight: 8,
  },

  selectorPlaceholder: {
    color: '#9ca3af',
    fontWeight: '600',
  },

  formCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    borderWidth: 1,
    borderColor: '#ececec',
    marginBottom: 14,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 2,
  },

  formCardTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 14,
  },

  inputGroup: {
    marginBottom: 12,
  },

  inputLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 8,
  },

  inputField: {
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#ececec',
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'web' ? 15 : 13,
    fontSize: 16,
    color: '#111827',
    outlineStyle: 'none',
  },

  summaryCard: {
    backgroundColor: '#111827',
    borderRadius: 24,
    padding: 18,
    marginBottom: 20,
  },

  summaryTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#fff',
    marginBottom: 14,
  },

  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },

  summaryLabel: {
    fontSize: 14,
    color: '#9ca3af',
    marginRight: 12,
  },

  summaryValue: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '700',
    flexShrink: 1,
    textAlign: 'right',
  },

  submitButton: {
    marginTop: 10,
    backgroundColor: '#FDF05D',
    borderRadius: 18,
    paddingVertical: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#f0de38',
  },

  submitButtonDisabled: {
    opacity: 0.65,
  },

  submitButtonText: {
    fontSize: 18,
    fontWeight: '800',
    color: '#111827',
  },

  submitButtonSubtext: {
    fontSize: 12,
    color: '#4b5563',
    marginTop: 4,
  },

  footerWrap: {
    alignItems: 'center',
    paddingVertical: 10,
  },

  footerLogo: {
    fontSize: 17,
    color: '#111827',
    fontWeight: '600',
    letterSpacing: 0.2,
  },

  footerDot: {
    color: '#FDF05D',
    fontSize: 19,
    fontWeight: '800',
  },

  footerText: {
    marginTop: 6,
    fontSize: 12,
    color: '#6b7280',
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,24,39,0.42)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },

  sheetCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
  },

  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },

  sheetTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111827',
  },

  timeScrollArea: {
    maxHeight: 380,
  },

  timeOption: {
    paddingVertical: 15,
    paddingHorizontal: 14,
    borderRadius: 14,
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#efefef',
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  timeOptionSelected: {
    backgroundColor: '#FDF05D',
    borderColor: '#f0de38',
  },

  timeOptionText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },

  timeOptionTextSelected: {
    color: '#111827',
  },

  partyGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },

  partyOption: {
    width: '23.5%',
    aspectRatio: 1,
    borderRadius: 16,
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#efefef',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },

  partyOptionSelected: {
    backgroundColor: '#FDF05D',
    borderColor: '#f0de38',
  },

  partyOptionText: {
    fontSize: 20,
    fontWeight: '800',
    color: '#111827',
  },

  partyOptionTextSelected: {
    color: '#111827',
  },

  successCard: {
    width: '86%',
    maxWidth: 420,
    backgroundColor: '#fff',
    padding: 24,
    borderRadius: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.16,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 12,
    elevation: 8,
  },

  successTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#111827',
    textAlign: 'center',
  },

  successSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 18,
    lineHeight: 21,
  },

  successButton: {
    backgroundColor: '#111827',
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 999,
  },

  successButtonText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 15,
  },

  errorModalCard: {
    width: '86%',
    maxWidth: 420,
    backgroundColor: '#fff',
    padding: 24,
    borderRadius: 24,
    alignItems: 'center',
  },

  errorModalText: {
    fontSize: 15,
    color: '#374151',
    textAlign: 'center',
    marginTop: 12,
    marginBottom: 20,
    lineHeight: 22,
  },

  errorModalButton: {
    backgroundColor: '#111827',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 999,
  },

  errorModalButtonText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 15,
  },

  errorCard: {
    width: '88%',
    maxWidth: 420,
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 6,
  },

  errorTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111827',
    marginTop: 8,
    marginBottom: 8,
    textAlign: 'center',
  },

  errorText: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 10,
  },

  errorBrand: {
    fontSize: 14,
    color: '#9ca3af',
    marginBottom: 18,
  },

  retryButton: {
    backgroundColor: '#111827',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 999,
  },

  retryButtonText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 15,
  },
});